import React from 'react';

import VideoJS from './videojs';

export default function Player(props) {
	const type = props.type ? props.type : 'videojs-internal';

	if (type === 'videojs-internal' || type === 'videojs-public') {
		const config = {
			controls: props.controls,
			poster: props.poster,
			autoplay: type === 'videojs-internal' ? true : props.autoplay ? (props.mute === 'muted' ? true : false) : false,
			muted: type === 'videojs-internal' ? 'muted' : props.mute,
			liveui: true,
			responsive: true,
			fluid: true,
			// In-band CEA-608/708 closed captions (ATSC A/53 Part 4 SEI) are already in
			// the delivered stream. These options are what make the player show them.
			// Everything below was read out of the installed trees, not from memory:
			//
			// * video.js only paints caption cues itself while the tech is not leaning on
			//   the browser's native text tracks. node_modules/video.js/dist/video.cjs.js:11260
			//   is `if (player.tech_ && player.tech_.featuresNativeTextTracks) { this.hide(); return; }`,
			//   and that flag defaults to Html5.supportsNativeTextTracks(), which is
			//   `IS_ANY_SAFARI || IS_IOS && IS_CHROME` (same file:20677) -- i.e. true on
			//   Safari/iOS, where video.js hands rendering to the browser and our in-band
			//   captions never become cue-bearing tracks it can draw. `native*Tracks: false`
			//   are the documented Html5 tech options that turn that off, switching to
			//   emulated tracks plus video.js's own renderer (same file:8317-8329, which
			//   then calls this.emulateTextTracks()).
			//
			// * The caption tracks only exist if VHS is the engine actually doing the
			//   playing: VHS turns the parsed CC1-CC4 and DTVCC services into real caption
			//   text tracks (node_modules/@videojs/http-streaming/README.md:215 "CEA-608
			//   captions are automatically translated into standard HTML5 caption text
			//   tracks"; src/util/text-tracks.js:50 adds them with kind: 'captions').
			//   VHS's own `overrideNative` defaults to `!(IS_ANY_SAFARI || IS_IOS)`
			//   (src/videojs-http-streaming.js:1398), so without it Safari/iOS keeps the
			//   native HLS engine and VHS never runs its caption path. overrideNative
			//   requires emulated audio/video tracks or VhsHandler throws (same file:636),
			//   which is why the two flags above belong to this block -- exactly the recipe
			//   in that README:325-345.
			//
			// * parse708captions is VHS's own default (README.md:318-323). Spelled out so
			//   the switch that drops CEA-708 services from the captions menu cannot be
			//   flipped by accident.
			//
			// Tracks arrive with kind 'captions' and `default: false`
			// (http-streaming/src/util/text-tracks.js:54), so nothing is forced on:
			// viewers turn them on in the captions menu, which video.js's default control
			// bar already carries (video.cjs.js:17799 lists 'subsCapsButton').
			html5: {
				nativeAudioTracks: false,
				nativeVideoTracks: false,
				nativeTextTracks: false,
				vhs: {
					overrideNative: true,
					parse708captions: true,
				},
			},
			plugins: {
				reloadSourceOnError: {},
			},
			sources: [{ src: props.source, type: 'application/x-mpegURL' }],
		};

		return (
			<VideoJS
				type={type}
				options={config}
				onReady={(player) => {
					if (props.logo.image.length !== 0) {
						var overlay = null;

						var imgTag = new Image();
						imgTag.onLoad = function () {
							imgTag.setAttribute('width', this.width);
							imgTag.setAttribute('height'.this.height);
						};
						imgTag.src = props.logo.image + '?' + Math.random();

						if (props.logo.link.length !== 0) {
							var aTag = document.createElement('a');
							aTag.setAttribute('href', props.logo.link);
							aTag.setAttribute('target', '_blank');
							aTag.appendChild(imgTag);
							overlay = aTag.outerHTML;
						} else {
							overlay = imgTag.outerHTML;
						}

						if (player.overlay) {
							player.overlay({
								align: props.logo.position,
								overlays: [
									{
										showBackground: false,
										content: overlay,
										start: 'playing',
										end: 'pause',
									},
								],
							});
						}
					}

					if (props.autoplay === true) {
						// https://videojs.com/blog/autoplay-best-practices-with-video-js/
						const p = player.play();

						if (!p) {
							// no autoplay;
						} else {
							p.then(
								() => {
									// autoplay worked;
								},
								() => {
									// autoplay did not work
								}
							);
						}
					}
				}}
			/>
		);
	}
}

Player.defaultProps = {
	type: 'videojs-internal',
	source: '',
	poster: '',
	controls: false,
	autoplay: false,
	mute: false,
	logo: {
		image: '',
		position: 'top-right',
		link: '',
	},
	ga: {
		account: '',
		name: '',
	},
	colors: {
		seekbar: '#fff',
		buttons: '#fff',
	},
	statistics: false,
};
